import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { MessageSquare, Camera, Send, Loader2 } from 'lucide-react';

/**
 * Dialog for admin/tech to add a control comment with rating and photos.
 * The comment is sent to the housekeeper via email.
 */
const ControlCommentDialog = ({
  open,
  onOpenChange,
  controlComment,
  setControlComment,
  controlRating,
  setControlRating,
  controlPhotos,
  setControlPhotos,
  controlPhotosPreviews,
  setControlPhotosPreviews,
  onSubmit,
  actionLoading
}) => {
  const photoInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const newPhotos = [...controlPhotos, ...files].slice(0, 10);
    setControlPhotos(newPhotos);
    
    // Generate previews
    const newPreviews = [...controlPhotosPreviews];
    files.forEach(file => {
      if (newPreviews.length < 10) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setControlPhotosPreviews(prev => [...prev, reader.result].slice(0, 10));
        };
        reader.readAsDataURL(file);
      }
    });
    
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    const newPhotos = [...controlPhotos];
    newPhotos.splice(idx, 1);
    setControlPhotos(newPhotos);
    
    const newPreviews = [...controlPhotosPreviews];
    newPreviews.splice(idx, 1);
    setControlPhotosPreviews(newPreviews);
  };

  const handleClose = () => {
    onOpenChange(false);
    setControlPhotos([]);
    setControlPhotosPreviews([]);
    setControlRating(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-500" />
            Évaluation du ménage
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {/* Rating Section */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <Label className="text-sm font-medium text-amber-800 mb-3 block">Note du ménage (sur 10)</Label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setControlRating(controlRating === num ? null : num)}
                  className={`w-10 h-10 rounded-lg font-bold text-lg transition-all ${
                    controlRating === num
                      ? 'bg-amber-500 text-white shadow-lg scale-110'
                      : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            {controlRating && (
              <p className="text-sm text-amber-600 mt-2">
                Note sélectionnée : <strong>{controlRating}/10</strong>
              </p>
            )}
          </div>

          <p className="text-sm text-gray-600">
            Ce commentaire sera envoyé par email à la femme de ménage pour lui indiquer les points à améliorer.
          </p>
          <Textarea
            placeholder="Ex: Merci pour votre travail. Points à améliorer : les draps étaient mal tirés, penser à bien nettoyer sous les meubles..."
            value={controlComment}
            onChange={(e) => setControlComment(e.target.value)}
            rows={5}
            className="resize-none"
            data-testid="control-comment-input"
          />
          
          {/* Photo upload section */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium mb-2 block">Photos (optionnel)</Label>
            <p className="text-xs text-gray-500 mb-3">Ajoutez des photos pour illustrer vos remarques</p>
            
            <input
              type="file"
              accept="image/*"
              multiple
              ref={photoInputRef}
              onChange={handlePhotoSelect}
              className="hidden"
            />
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => photoInputRef.current?.click()}
              className="mb-3"
            >
              <Camera className="h-4 w-4 mr-2" />
              Ajouter des photos ({controlPhotos.length}/10)
            </Button>
            
            {/* Photo previews */}
            {(controlPhotosPreviews || []).length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {(controlPhotosPreviews || []).map((preview, idx) => (
                  <div key={`control-preview-${idx}`} className="relative group">
                    <img
                      src={preview || ''}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-16 object-cover rounded-lg border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Annuler</Button>
          <Button 
            className="bg-[#003366] hover:bg-[#002244]" 
            onClick={onSubmit} 
            disabled={actionLoading || !controlComment.trim()} 
            data-testid="submit-comment-btn"
          >
            {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            <Send className="h-4 w-4 mr-2" />
            Envoyer {controlRating && `(${controlRating}/10)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ControlCommentDialog;
